import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { fetchNodeTimeseries } from '../../api';
import { buildNodeParams } from './nodeFilterUtils';
import useNodeRequest from './useNodeRequest';
import { AXIS, formatCalls, formatShare, PLOT_AREA_CLASS, resolveCodeColor } from './nodeChartTheme';

/**
 * Динамика значений выхода по периодам.
 *
 * Столбцы стековые: у выхода обычно несколько значений, и рядом стоящие столбики
 * отвечают на вопрос «чего больше», а стек — на вопрос «сколько всего звонков за день
 * и как они поделились», который здесь и задают.
 *
 * Цвет серии берётся тем же `resolveCodeColor`, что и в карточке распределения, поэтому
 * категория не меняет цвет при переключении «Распределение ↔ Динамика».
 */
const GRANULARITIES = [
  { code: 'day', label: 'По дням' },
  { code: 'week', label: 'По неделям' },
  { code: 'month', label: 'По месяцам' }
];

const formatBucket = (bucket) => {
  const date = new Date(bucket);
  return Number.isNaN(date.getTime())
    ? bucket
    : date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
};

const TimeseriesTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div
      className="rounded-md border px-3 py-2 text-sm shadow-md max-sm:max-w-[220px] max-sm:text-xs"
      style={{
        background: 'hsl(var(--card))',
        color: 'hsl(var(--card-foreground))',
        borderColor: 'hsl(var(--border))'
      }}
    >
      <div className="mb-1 font-medium">{label}</div>
      {payload
        .filter((entry) => entry.value > 0)
        .map((entry) => (
          <div key={entry.dataKey} className="flex items-center gap-2">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
            <span>
              {entry.name}: {formatCalls(entry.value)}
              {/* Доля — от звонков, обработанных нодой в этом периоде; сервер отдаёт
                  знаменатель отдельно, иначе проценты не складывались бы в 100%. */}
              {entry.payload?.[`${entry.dataKey}__share`] !== undefined &&
                ` (${formatShare(entry.payload[`${entry.dataKey}__share`])})`}
            </span>
          </div>
        ))}
    </div>
  );
};

const NodeTimeseriesChart = ({ chart, dateRange, callType, nodeFilter, packVersion, scope, height }) => {
  const [granularity, setGranularity] = useState('day');
  const [view, setView] = useState('bar');

  const params = useMemo(
    () => ({ ...buildNodeParams({ chart, dateRange, callType, nodeFilter, packVersion, scope }), granularity }),
    [chart, dateRange, callType, nodeFilter, packVersion, scope, granularity]
  );

  const { data, error } = useNodeRequest({
    fetcher: fetchNodeTimeseries,
    params,
    errorText: 'Не удалось загрузить динамику по узлу.',
    enabled: Boolean(params.date_from && params.date_to)
  });

  const { rows, series } = useMemo(() => {
    const points = Array.isArray(data) ? data : [];
    const buckets = [...new Set(points.map((point) => point.bucket))].sort();
    const codes = [...new Set(points.map((point) => point.code))];

    const labelByCode = new Map(points.map((point) => [point.code, point.label || point.code]));
    const byBucket = new Map(buckets.map((bucket) => [bucket, { bucket, label: formatBucket(bucket) }]));

    points.forEach((point) => {
      const row = byBucket.get(point.bucket);
      row[point.code] = Number(point.calls) || 0;
      row[`${point.code}__share`] = Number(point.share) || 0;
    });

    const usedColors = new Set();

    return {
      rows: buckets.map((bucket) => byBucket.get(bucket)),
      series: codes.map((code, index) => ({
        code,
        label: labelByCode.get(code) ?? code,
        color: resolveCodeColor(code, index, { tone: 'categorical', usedColors })
      }))
    };
  }, [data]);

  if (error) {
    return <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>;
  }

  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">Нет данных за выбранный период.</p>;
  }

  const Chart = view === 'bar' ? BarChart : LineChart;

  return (
    <div className="flex flex-1 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-md border p-0.5">
          {['bar', 'line'].map((mode) => (
            <button
              key={mode}
              type="button"
              data-ev="nodes.timeseries.view"
              data-ev-value={mode}
              onClick={() => setView(mode)}
              className={`rounded px-2 py-0.5 text-xs ${
                view === mode ? 'bg-muted font-medium' : 'text-muted-foreground hover:bg-muted/50'
              }`}
            >
              {mode === 'bar' ? 'Столбцы' : 'Линия'}
            </button>
          ))}
        </div>

        <select
          value={granularity}
          data-ev="nodes.timeseries.granularity"
          data-ev-send-value
          onChange={(event) => setGranularity(event.target.value)}
          className="h-7 rounded-md border bg-background px-2 text-xs"
        >
          {GRANULARITIES.map((option) => (
            <option key={option.code} value={option.code}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className={PLOT_AREA_CLASS} style={{ minHeight: height }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
          <Chart data={rows} margin={{ top: 12, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid {...AXIS.grid} />
            <XAxis dataKey="label" tick={AXIS.tick} />
            <YAxis width={44} tick={AXIS.tick} allowDecimals={false} />
            <Tooltip content={<TimeseriesTooltip />} wrapperStyle={AXIS.tooltipWrapperStyle} />
            {series.map((item) =>
              view === 'bar' ? (
                <Bar
                  key={item.code}
                  dataKey={item.code}
                  name={item.label}
                  stackId="calls"
                  fill={item.color}
                  isAnimationActive={false}
                  maxBarSize={AXIS.maxBarSize}
                />
              ) : (
                <Line
                  key={item.code}
                  type="monotone"
                  dataKey={item.code}
                  name={item.label}
                  stroke={item.color}
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  isAnimationActive={false}
                />
              )
            )}
          </Chart>
        </ResponsiveContainer>
      </div>

      <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {series.map((item) => (
          <li key={item.code} className="flex items-center gap-1.5">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
            {item.label}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default NodeTimeseriesChart;
